import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';
import { cn } from '@/shared/utils/cn/cn.util';

import { Button } from '../../ui/Button/Button';

export interface ErrorStateProps {
  error: ApiError;
  /** Shown instead of the kit's generic title. */
  title?: string;
  /** Omit when there is nothing to retry — a dead retry button is worse than none. */
  onRetry?: () => void;
  className?: string;
}

/**
 * A failed read.
 *
 * The server's own `message` is preferred over kit copy because the api already
 * localises it (`Accept-Language` goes out on every request) and it is far more
 * specific than "Something went wrong" — which is only used when the server
 * genuinely said nothing, as on a transport failure.
 *
 * `role="alert"` rather than `status`: a read that failed has to interrupt. An
 * operator who does not notice concludes the records were deleted.
 */
export function ErrorState({ error, title, onRetry, className }: ErrorStateProps) {
  const { t } = useTranslation('uiKit');

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-md px-xl py-3xl text-center',
        className,
      )}
    >
      <AlertTriangle aria-hidden className="h-8 w-8 text-destructive" />
      <p className="text-h3 text-foreground">{title ?? t('state.errorTitle')}</p>
      {error.message === '' ? null : (
        <p className="text-caption text-muted-foreground">{error.message}</p>
      )}
      {onRetry === undefined ? null : (
        <Button variant="outline" onClick={onRetry}>
          {t('action.retry', { ns: 'common' })}
        </Button>
      )}
    </div>
  );
}
